import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import axios from 'axios';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { API_URL } from "../constants";
import { useNavigate } from 'react-router-dom';
import Toast from './Toast';

const AddClientLocation = ({ motor, token, initialClient = '', initialLocalisation = '', onSuccess, onCancel }) => {
  const [client, setClient] = useState(initialClient);
  const [localisation, setLocalisation] = useState(initialLocalisation);
  const [suggestions, setSuggestions] = useState([]);
  const [filteredClientList] = useState([]);
  const selectedFromMap = React.useRef(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 46.603354, lng: 1.888334 });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '' });

  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyAkoDqDB5nScUOm1PZQXos4KDzqG-5-6xQ',
  });

  useEffect(() => {
    setClient(initialClient);
    setLocalisation(initialLocalisation);
  }, [initialClient, initialLocalisation]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast({ type: '', message: '' }), 3000);
  };

  const handleClientChange = (event) => {
    const searchText = event.target.value;
    setClient(searchText);
  };

  const handleLocalisationChange = async (e) => {
    const searchText = e.target.value;
    setLocalisation(searchText);
    selectedFromMap.current = false;

    if (searchText.length >= 4) {
      const encodedText = encodeURIComponent(searchText).replace(/%20/g, '+');
      try {
        const response = await axios.get(
          `https://api-adresse.data.gouv.fr/search/?q=${encodedText}`
        );
        setSuggestions(response.data.features.map(feature => ({
          ...feature.properties,
          coordinates: feature.geometry.coordinates,
        })));
      } catch (error) {
        console.error(error);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setLocalisation(suggestion.label);
    setSuggestions([]);
    selectedFromMap.current = true;

    const coords = {
      lat: suggestion.coordinates[1],
      lng: suggestion.coordinates[0],
    };
    setSelectedLocation(coords);
    setMapCenter(coords);
    setShowMap(true);
  };

  const handleClientLocSelect = (selectedClient, selectedLocalisation) => {
    setClient(selectedClient);
    setLocalisation(selectedLocalisation);
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMapCenter({ lat, lng });
    setSelectedLocation({ lat, lng });
    selectedFromMap.current = true;

    axios.get(`https://api-adresse.data.gouv.fr/reverse/?lat=${lat}&lon=${lng}`)
      .then(response => {
        const data = response.data;
        if (!data.features || data.features.length === 0) return;

        const props = data.features[0].properties;
        const address = {
          streetNumber: props.housenumber || '',
          streetName: props.street || '',
          postalCode: props.postcode || '',
          city: props.city || props.town || props.village || '',
        };
        const formattedAddress = `${address.streetNumber} ${address.streetName}, ${address.postalCode} ${address.city}`.trim();
        setLocalisation(formattedAddress);
        setSuggestions([]);
      })
      .catch(error => console.error(error));
  };

  const handleClientLocSubmit = async () => {
    if (!client || !localisation) return;
    setSubmitting(true);
    try {
      const response = await axios.put(`${API_URL}/camera/setclientloc`, {
        client: client,
        localisation: localisation,
        id_camera: motor.id,
      }, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      showToast('success', 'Client et localisation enregistrés !');
      setTimeout(() => onSuccess?.(response.data), 600);
    } catch (error) {
      if (error.response?.status === 401) {
        alert("ERREUR : Votre session a expiré. Veuillez vous reconnecter pour continuer.");
        localStorage.clear();
        navigate("/login");
        return;
      }
      console.error(error);
      showToast('error', "Échec de l'enregistrement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-5 space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Nom du client"
          value={client}
          onChange={handleClientChange}
          className="flex-1 border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          placeholder="Localisation"
          value={localisation}
          onChange={handleLocalisationChange}
          className="flex-1 border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <button
        onClick={() => setShowMap((prev) => !prev)}
        className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-5 py-3 rounded-lg shadow-sm hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 font-medium text-base"
      >
        <FaMapMarkerAlt className="text-blue-500" />
        {showMap ? 'Masquer la carte' : 'Choisir sur la carte'}
      </button>

      {showMap && isLoaded && (
        <div className="rounded-lg overflow-hidden border border-gray-200">
          <GoogleMap
            mapContainerStyle={{ height: "350px", width: "100%" }}
            center={selectedLocation ? selectedLocation : mapCenter}
            zoom={selectedLocation ? 15 : 6}
            onClick={handleMapClick}
          >
            {selectedLocation && (
              <Marker position={selectedLocation} />
            )}
          </GoogleMap>
        </div>
      )}

      {suggestions.length > 0 && (
        <ul className="space-y-2 max-h-48 overflow-y-auto">
          {suggestions.map((address, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionSelect(address)}
              className="flex items-center gap-3 px-4 py-3 cursor-pointer bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-150"
            >
              <FaMapMarkerAlt className="text-blue-400 shrink-0" />
              {address.label}
            </li>
          ))}
        </ul>
      )}

      {client && filteredClientList.length > 0 && (
        <ul className="space-y-2 max-h-48 overflow-y-auto">
          {filteredClientList.map((item, index) => (
            <li
              key={index}
              onClick={() => handleClientLocSelect(item.client, item.localisation)}
              className="px-4 py-3 cursor-pointer bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-150"
            >
              {item.client} — {item.localisation}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-3 pt-1">
        <button
          onClick={handleClientLocSubmit}
          disabled={submitting || !client || !localisation}
          className="flex-1 sm:flex-none bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-medium disabled:opacity-50"
        >
          {submitting ? 'Enregistrement...' : 'Valider'}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          className="bg-white border border-gray-300 text-gray-600 px-6 py-3 rounded-lg shadow-sm hover:bg-gray-100 transition-all duration-200 font-medium disabled:opacity-50"
        >
          Annuler
        </button>
      </div>

      <Toast type={toast.type} message={toast.message} />
    </div>
  );
};

export default AddClientLocation;